import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Checkbox,
  Space,
  Tag,
  message,
  Typography,
  Divider,
  Form,
  Input
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  PlusOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import PermissionGuard from '../components/PermissionGuard';

const { Text } = Typography;


// 模块名称映射
const moduleNameMap = {
  'user': '用户管理',
  'role': '角色管理',
  'permission': '权限管理',
  'lead': '人才CRM',
  'interview': '面试管理',
  'employee': '员工管理',
  'onboarding': '入职管理',
  'settings': '系统配置'
};

const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false);
  const [addRoleForm] = Form.useForm();

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  // 当角色列表加载完成后，自动获取所有角色的权限数据
  useEffect(() => {
    if (roles.length > 0) {
      roles.forEach(role => {
        fetchRolePermissions(role.name);
      });
    }
  }, [roles]);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/users/roles/list');
      setRoles(response.data);
    } catch (error) {
      message.error('获取角色列表失败');
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/users/permissions/list');
      setPermissions(response.data);
    } catch (error) {
      message.error('获取权限列表失败');
    }
  };

  const fetchRolePermissions = async (roleName) => {
    try {
      const response = await api.get(`/users/roles/${roleName}/permissions`);
      setRolePermissions(prev => ({
        ...prev,
        [roleName]: response.data.map(p => p.name)
      }));
    } catch (error) {
      console.error('获取角色权限失败:', error);
    }
  };

  const handlePermissionSetting = async (role) => {
    setSelectedRole(role);
    if (!rolePermissions[role.name]) {
      await fetchRolePermissions(role.name);
    }
    setSelectedPermissions(rolePermissions[role.name] || []);
    setModalVisible(true);
  };

  const handleSavePermissions = async () => {
    try {
      await api.put(`/users/roles/${selectedRole.name}/permissions`, {
        permissions: selectedPermissions
      });
      
      // 立即刷新该角色的权限数据
      await fetchRolePermissions(selectedRole.name);
      
      message.success('权限更新成功');
      setModalVisible(false);
    } catch (error) {
      message.error('权限更新失败');
    }
  };

  const handlePermissionChange = (permission, checked) => {
    if (checked) {
      setSelectedPermissions(prev => [...prev, permission]);
    } else {
      setSelectedPermissions(prev => prev.filter(p => p !== permission));
    }
  };

  const handleAddRole = () => {
    setAddRoleModalVisible(true);
    addRoleForm.resetFields();
  };

  const handleAddRoleSubmit = async (values) => {
    try {
      await api.post('/users/roles', values);
      message.success('角色创建成功');
      setAddRoleModalVisible(false);
      fetchRoles(); // 重新获取角色列表
    } catch (error) {
      message.error(error.response?.data?.message || '创建角色失败');
    }
  };

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: '角色标识',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Tag color="blue">{name}</Tag>
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '权限数量',
      key: 'permissionCount',
      render: (_, record) => {
        const count = rolePermissions[record.name]?.length || 0;
        return <Tag color="green">{count} 个权限</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <PermissionGuard permission="permission.assign">
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => handlePermissionSetting(record)}
            >
              权限设置
            </Button>
          </PermissionGuard>
        </Space>
      )
    }
  ];

  // 按模块分组权限
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const module = permission.module;
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {});

  return (
    <div>
      <Card 
        title="角色权限管理" 
        extra={
          <PermissionGuard permission="role.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRole}>
              新增角色
            </Button>
          </PermissionGuard>
        }
      >
        <Table
          columns={columns}
          dataSource={roles}
          rowKey="name"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={`权限设置 - ${selectedRole?.display_name}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            取消
          </Button>,
          <PermissionGuard permission="permission.assign">
            <Button
              key="save"
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSavePermissions}
            >
              保存权限
            </Button>
          </PermissionGuard>
        ]}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
            <div key={module} style={{ marginBottom: '24px' }}>
              <Divider orientation="left">
                <Text strong>
                  {moduleNameMap[module] || module} 模块
                </Text>
              </Divider>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {modulePermissions.map(permission => (
                  <Checkbox
                    key={permission.name}
                    checked={selectedPermissions.includes(permission.name)}
                    onChange={(e) => handlePermissionChange(permission.name, e.target.checked)}
                  >
                    <Text>{permission.display_name}</Text>
                  </Checkbox>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* 新增角色模态框 */}
      <Modal
        title="新增角色"
        open={addRoleModalVisible}
        onCancel={() => setAddRoleModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={addRoleForm}
          layout="vertical"
          onFinish={handleAddRoleSubmit}
        >
          <Form.Item
            name="name"
            label="角色标识"
            rules={[
              { required: true, message: '请输入角色标识' },
              { pattern: /^[a-z_]+$/, message: '角色标识只能包含小写字母和下划线' }
            ]}
            extra="角色标识用于系统内部识别，只能包含小写字母和下划线"
          >
            <Input placeholder="例如：hr_specialist" />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
            extra="角色名称用于界面显示"
          >
            <Input placeholder="例如：人事专员" />
          </Form.Item>

          <Form.Item
            name="description"
            label="角色描述"
            extra="可选，用于描述角色的职责和权限范围"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="请输入角色描述..."
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setAddRoleModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                创建角色
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RolePermissions; 